import React, { useState, useEffect } from "react";
import "../styles/intake.css";
import RoleSelector from "../components/forms/RoleSelector";
import ServiceSelector from "../components/forms/ServiceSelector";
import LanguageSelector from "../components/forms/LanguageSelector";

export default function StaffForm({ formData, setFormData }) {
  const [staffList, setStaffList] = useState([]);
  const [isEditing, setIsEditing] = useState(true);

  useEffect(() => {
    const fetchCertifications = async (staffId) => {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/staff/get-staff-certifications?staff_id=${staffId}`);
      return res.json();
    };

    if (formData.clinic_id) {
      fetch(`${process.env.REACT_APP_API_BASE_URL}/staff/get-staff?clinic_id=${formData.clinic_id}`)
        .then((res) => res.json())
        .then(async (data) => {
          const staffWithCerts = await Promise.all(
            data.map(async (staff) => {
              const certs = await fetchCertifications(staff.id);
              return { ...staff, certifications: certs || [] };
            })
          );
          setStaffList(staffWithCerts);
        })
        .catch((err) => {
          console.error("Failed to load staff:", err);
          setStaffList([]);
        });
    }
  }, [formData.clinic_id]);

  const addStaff = () => {
    setStaffList([
      ...staffList,
      {
        id: null,
        name: "",
        title: "",
        role: [],
        services: [],
        phone: "",
        email: "",
        languages: [],
        available_days: [],
        certifications: [],
      },
    ]);
  };

  const updateStaff = (index, field, value) => {
    const updated = [...staffList];
    updated[index][field] = value;
    setStaffList(updated);
  };

  const updateAvailability = (index, day) => {
    const updated = [...staffList];
    const days = updated[index].available_days;
    updated[index].available_days = days.includes(day)
      ? days.filter((d) => d !== day)
      : [...days, day];
    setStaffList(updated);
  };

  const removeStaff = async (index) => {
    const staff = staffList[index];
  
    // If staff has been saved before (has ID), delete from DB
    if (staff.id) {
      const confirmed = window.confirm("Are you sure you want to delete this staff member?");
      if (!confirmed) return;
  
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/staff/delete-staff/${staff.id}`,
          {
            method: "DELETE",
          }
        );
  
        const result = await res.json();
  
        if (!res.ok) {
          throw new Error(result.message || "Failed to delete staff");
        }
  
        alert("✅ Staff deleted successfully!");
      } catch (err) {
        alert("❌ Failed to delete from backend: " + err.message);
        return;
      }
    }
  
    // Remove from frontend
    const updated = [...staffList];
    updated.splice(index, 1);
    setStaffList(updated);
  };
  

  const handleFileUpload = async (file, clinicId, staffId) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("clinic_id", clinicId);
    formData.append("staff_id", staffId);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/staff/upload-staff-certification`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        alert("✅ Certification uploaded successfully!");
        return result.url;
      } else {
        console.error("Upload failed", result.error || result.message);
        alert("❌ Upload failed: " + (result.error || result.message));
      }
    } catch (err) {
      console.error("Upload error", err);
      alert("❌ Upload error: " + err.message);
    }
  };

  const handleDeleteCertification = async (staffId, fileUrl) => {
    const confirmed = window.confirm("Are you sure you want to delete this certification?");
    if (!confirmed) return;
  
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/staff/delete-staff-certification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ staff_id: staffId, file_url: fileUrl }),
      });
  
      if (!res.ok) {
        const error = await res.json();
        console.error("Delete failed:", error.detail);
        alert("Delete failed: " + error.detail);
      } else {
        // ✅ Remove from UI
        setStaffList((prevList) =>
          prevList.map((s) =>
            s.id === staffId
              ? { ...s, certifications: s.certifications.filter((c) => c.file_url !== fileUrl) }
              : s
          )
        );
      }
    } catch (err) {
      console.error("Error:", err);
      alert("Error deleting certification.");
    }
  };
  
  

  const handleSave = async (staff) => {
    try {
      const cleanStaff = {
        id: staff.id,
        clinic_id: formData.clinic_id,
        name: staff.name,
        title: staff.title,
        role: staff.role,
        services: staff.services,
        phone: staff.phone,
        email: staff.email,
        languages: staff.languages,
        available_days: staff.available_days,
      };

      console.log("📤 Saving staff:", cleanStaff);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/staff/save-staff`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cleanStaff),
      });

      const result = await response.json();

      if (response.ok) {
        alert("✅ Staff member added and saved successfully!");
        return result.staff_id;
      } else {
        const errorMessage = Array.isArray(result.detail)
          ? result.detail.map((e) => e.msg).join(", ")
          : typeof result.detail === "string"
          ? result.detail
          : JSON.stringify(result.detail || result);
        alert("❌ Failed to save staff: " + errorMessage);
      }
    } catch (err) {
      console.error("❌ Error saving staff:", err);
      alert("❌ Error saving staff: " + err.message);
    }
  };

  const daysOfWeek = [
    "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Holiday"
  ];

  return (
    <div className="form-wrapper">
      <div className="form-section-header">
        <h2>3. <span className="section-title">Staff</span></h2>
        <div className="form-buttons">
          {isEditing ? (
            <button className="blue-button" onClick={() => handleSave(staffList[staffList.length - 1])}>Save</button>
          ) : (
            <button className="blue-button" onClick={() => setIsEditing(true)}>Edit</button>
          )}
        </div>
      </div>

      <div className="separator" />

      {staffList.length === 0 && (
        <div className="staff-add-button">
          <button className="add-button" onClick={addStaff}>+ Add Staff</button>
        </div>
      )}

      {staffList.map((staff, index) => (
        <div key={index} className="staff-block">
          <div className="staff-form-grid">
            <div className="form-field">
              <label>Full Name<span className="required">*</span></label>
              <input
                type="text"
                value={staff.name}
                onChange={(e) => updateStaff(index, "name", e.target.value)}
              />
            </div>

            <div className="form-field">
              <label>Title<span className="required">*</span></label>
              <input
                type="text"
                value={staff.title}
                onChange={(e) => updateStaff(index, "title", e.target.value)}
              />
            </div>

            <div className="dropdown-item3">
              <label>Role<span className="required">*</span></label>
              <RoleSelector
                selectedRoles={staff.role}
                onChange={(roles) => updateStaff(index, "role", roles)}
              />
            </div>

            <div className="dropdown-item3">
              <label>Services</label>
              <ServiceSelector
                selected={staff.services}
                setSelected={(value) => updateStaff(index, "services", value)}
              />
            </div>

            <div className="form-field">
              <label>Phone</label>
              <input
                type="tel"
                value={staff.phone}
                onChange={(e) => updateStaff(index, "phone", e.target.value)}
              />
            </div>

            <div className="form-field">
              <label>Email</label>
              <input
                type="email"
                value={staff.email}
                onChange={(e) => updateStaff(index, "email", e.target.value)}
              />
            </div>

            <div className="dropdown-item3">
              <label>Language</label>
              <LanguageSelector
                selectedLanguages={staff.languages}
                setSelectedLanguages={(langs) => updateStaff(index, "languages", langs)}
              />
            </div>

            <div className="form-field full-width">
              <label>Available Days</label>
              <div className="day-checkbox-group">
                {daysOfWeek.map((day) => (
                  <label key={day}>
                    <input
                      type="checkbox"
                      checked={staff.available_days.includes(day)}
                      onChange={() => updateAvailability(index, day)}
                    />
                    {day}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-field full-width">
              <label>Certification (Upload)</label>
              <input
                type="file"
                onChange={async (e) => {
                  const file = e.target.files[0];

                  if (!staffList[index].id) {
                    const newStaffId = await handleSave(staffList[index]);
                    if (newStaffId) {
                      updateStaff(index, "id", newStaffId);
                    } else {
                      alert("❌ Could not save staff. Cannot upload certification.");
                      return;
                    }
                  }

                  const url = await handleFileUpload(file, formData.clinic_id, staffList[index].id);
                  if (url) {
                    const updatedCerts = [...(staffList[index].certifications || []), { file_url: url, file_name: file.name }];
                    updateStaff(index, "certifications", updatedCerts);
                  }
                }}
              />
              {staff.certifications && staff.certifications.length > 0 && (
                <ul className="small-note">
                  {staff.certifications.map((cert, i) => (
                    <li key={i}>
                      ✅ <a href={cert.file_url} target="_blank" rel="noopener noreferrer">{cert.file_name}</a>
                      <button
                      onClick={() => handleDeleteCertification(staff.id, cert.file_url)}
                      style={{
                        color: "red",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "16px"
                      }}
                      title="Delete this certification"
                    >
                      ✕
                    </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="staff-actions">
            <button className="remove-button" onClick={() => removeStaff(index)}>- Remove</button>
            {index === staffList.length - 1 && (
              <button className="add-button" onClick={addStaff}>+ Add Staff</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
